import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import PageShell from "@/components/PageShell";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Mail, Lock, ArrowRight } from "lucide-react";
import { formatApiError } from "@/lib/api";

export default function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const next = sp.get("next") || "/dashboard";
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await login(form.email, form.password);
      toast.success(`Welcome back, ${data.name?.split(" ")[0] || "cultist"}.`);
      nav(data.role === "admin" ? "/admin" : next);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = formatApiError(detail) || "Login failed";
      // Detect unverified-email gate (403) and redirect to verification page
      if (err.response?.status === 403 && typeof msg === "string" && /verify your email/i.test(msg)) {
        toast.info("Please verify your email to continue.");
        nav(`/verify-email?email=${encodeURIComponent(form.email)}`);
        return;
      }
      toast.error(msg);
    } finally { setLoading(false); }
  };

  return (
    <PageShell noFooter>
      <div className="min-h-[80vh] grid place-items-center px-6 py-16">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="font-mystic text-rose-400 text-sm">門を開く</div>
            <h1 className="font-display text-3xl font-bold text-white mt-1">Return to the Cult</h1>
            <p className="text-zinc-400 text-sm mt-2">Sign in to continue your devotion.</p>
          </div>
          <form onSubmit={submit} className="glass-strong rounded-xl p-7 space-y-5">
            <div>
              <Label className="text-zinc-300 text-xs uppercase tracking-wider">Email</Label>
              <div className="relative mt-1.5">
                <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <Input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="pl-10 bg-black/40 border-white/10 text-white"
                  data-testid="login-email-input"
                />
              </div>
            </div>
            <div>
              <Label className="text-zinc-300 text-xs uppercase tracking-wider">Password</Label>
              <div className="relative mt-1.5">
                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <Input
                  type="password"
                  required
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="pl-10 bg-black/40 border-white/10 text-white"
                  data-testid="login-password-input"
                />
              </div>
            </div>
            <div className="flex justify-end">
              <Link to="/forgot-password" className="text-xs text-zinc-400 hover:text-rose-300" data-testid="login-forgot-link">Forgot password?</Link>
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-rose-600 hover:bg-rose-500 text-white font-semibold py-6"
              data-testid="login-submit-btn"
            >
              {loading ? "Signing in…" : (<>Sign In <ArrowRight className="w-4 h-4 ml-2" /></>)}
            </Button>
            <div className="text-center text-sm text-zinc-400">
              No account?{" "}
              <Link to="/register" className="text-rose-400 hover:text-rose-300 font-semibold" data-testid="login-go-register">
                Join the cult
              </Link>
            </div>
          </form>
        </div>
      </div>
    </PageShell>
  );
}
